import ReactGA from 'react-ga';

import { RealObject } from '@tb-core/types';

export type GaDataLayerConfig = RealObject<string | null | boolean | undefined>;
export type WindowDataLayer = Window &
    typeof globalThis & {
        dataLayer?: GaDataLayerConfig[];
    };
export interface UpdateAndClearClickEventProps {
    page: string;
    value: string;
}

let previousDataLayerPush: GaDataLayerConfig; // Holds custom data from previous data layer push

/**
 * Updates the data layer with events or custom data.
 * @param data The config to add to the dataLayer.
 *  https://developers.google.com/tag-manager/devguide#events
 *  https://developers.google.com/tag-manager/devguide#multipush
 * @example
 *      const onClickHandler = () => updateDataLayer({ event: 'event-name'});
 *      return (
 *          <button onClick={onClickHandler}>Track my click!</button>
 *      );
 * @example
 *      // when omitting the "event" key/value entry...
 *      updateDataLayer({
 *          'Analytics-Action': 'Left Nav',
 *          'Analytics-Value': 'success!'
 *      })
 *      // results in "Message" w/ dataLayer "{Analytics-Action: 'Left Nav', Analytics-Value: 'success!'}"
 */
export const updateDataLayer = (data: GaDataLayerConfig) => {
    // Set any custom keys that may be persisting from the previous push to undefined
    for (const key in previousDataLayerPush) {
        if (
            !data.hasOwnProperty(key) &&
            !key.includes('event') &&
            !key.includes('gtm.')
        ) {
            data[key] = undefined;
        }
    }

    previousDataLayerPush = data; // Update the object with the new data being pushed

    (window as WindowDataLayer).dataLayer?.push(data);
};

/**
 * Pushes a click event with 'Analytic-Action' set to the page and 'Analytics-Value' as the value provided.
 * After pushing to the dataLayer, an object with 'Analytics-Action' and 'Analytics-Value' will be pushed with the values set to
 * undefined. Helpful when multiple dataLayer pushes happen in a single page. Prevents subsequent dataLayer pushes
 * from inadvertently including 'Analytics-Action' and 'Analytics-Value' unless explicitly included.
 *  */

export const updateAndClearClickEvent = ({
    page,
    value
}: UpdateAndClearClickEventProps) => {
    updateDataLayer({
        'Analytics-Action': page,
        'Analytics-Value': value,
        event: 'gtm.click'
    });

    // Need to mark 'Analytics-Value' and 'Analytics-Action' as undefined to prevent
    // them from appearing in the next dataLayer update
    updateDataLayer({
        'Analytics-Action': undefined,
        'Analytics-Value': undefined
    });
};

/* [DEPRECATED] React GA */

type ReactGAWindow = Window & { gaInitialized?: boolean };

interface GATracker {
    gaOptions: { name: string };
    trackingId: string;
}

export function initGA(gaTrackers: GATracker[]) {
    const win: ReactGAWindow = window;
    if (win && !win.gaInitialized) {
        ReactGA.initialize(gaTrackers);
        win.gaInitialized = true;
    }
}

export function logPageView(trackerNames: string[]) {
    ReactGA.set({ page: window.location.pathname }, trackerNames);
    ReactGA.pageview(window.location.pathname, trackerNames);
}

export function logEvent(category = '', action = '', label = '') {
    if (category && action) {
        ReactGA.event({ category, action, label });
    }
}

export function gtmLinkClick(data: GaDataLayerConfig) {
    updateDataLayer({
        event: 'gtm.linkClick',
        ...data
    });
}
